import { IPassportGroup, IPassportSchema, IPassportSchemaField } from '@/pages/MDM/mdm.types.ts'
import MDMValueInput from '@/pages/MDM/components/MDMValueInput'
import FormField from '@/shared/Forms/FormField'
import usePassportSchema from '@/pages/MDM/hooks/usePassprotSchema.ts'
import { TCreatePassportValueDTO } from '@/pages/Equipment/equipment.types.ts'
import { forwardRef, useMemo, useRef } from 'react'
import objectUtils from '@/utils/object.utils.ts'
import useActiveObservableElement from '@/shared/hooks/useActiveObservableElement'

interface IProps {
  passportSchemaId: IPassportSchema['id']
  values: TCreatePassportValueDTO[]
  onChange: (index: number, value: any, key: string) => void
  onGroupIntoViewportChange?: (activeGroupId: IPassportGroup['id']) => void
}

const EquipmentPassportValuesForm = forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const passportSchema = usePassportSchema(props.passportSchemaId)

  const observingElements = useRef<{ element: HTMLElement; key: number }[]>([])

  useActiveObservableElement({
    elements: observingElements.current,
    onCurrentObservingElementChange: props.onGroupIntoViewportChange,
    active: Boolean(props.onGroupIntoViewportChange),
  })

  const groupedFields: Array<{ id: number; groupName: string; children: IPassportSchemaField[] }> = useMemo(() => {
    if (!passportSchema) return []

    const fieldsByGroup = objectUtils.arrToKeyArray(passportSchema.fields, 'groupId')
    const result: Array<{ id: number; groupName: string; children: IPassportSchemaField[] }> = []

    for (const group of passportSchema.groups) {
      if (fieldsByGroup[group.id]?.length) {
        result.push({
          id: group.id,
          groupName: group.name,
          children: [...fieldsByGroup[group.id]].sort((a, b) => a.orderingIndex - b.orderingIndex),
        })
      }
    }

    return result
  }, [passportSchema])

  const renderFieldInput = (field: IPassportSchemaField) => {
    const valIndex = props.values.findIndex((v) => v.fieldId === field.id)
    const val = props.values[valIndex]
    return (
      <MDMValueInput
        value={getPassportFieldValue(field, val)?.toString()}
        schema={field}
        name="value"
        placeHolder="Введите значение"
        onChange={(val, name) => props.onChange(valIndex, val, name)}
        onUnitValueChange={(val) => props.onChange(valIndex, val, 'unitValueId')}
        onEnumValueChange={(val) => props.onChange(valIndex, val, 'enumValueId')}
      />
    )
  }

  const renderFields = () => {
    if (!groupedFields?.length) return null

    return groupedFields.map((f, index) => (
      <div
        className="flex-lines gap16"
        key={f.id}
        ref={(el) => (observingElements.current[index] = { element: el, key: f.id })}
        id={`groupId_${f.id}`}
      >
        <h5 className="text-heading-5">{f.groupName}</h5>
        {f.children.map((c) => (
          <FormField label={c.name} key={c.id}>
            {renderFieldInput(c)}
          </FormField>
        ))}
      </div>
    ))
  }

  return (
    <div ref={ref} className="flex-lines gap16">
      {renderFields()}
    </div>
  )
})

const getPassportFieldValue = (schema: IPassportSchemaField, value: TCreatePassportValueDTO) => {
  if (!value) return null

  switch (schema.type) {
    case 'ENUM':
      return value.enumValueId
    case 'UNIT':
      return value.unitValueId
    default:
      return value.value
  }
}

export default EquipmentPassportValuesForm
